// src/contexts/OrderContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axiosInstance from "../utils/axiosInstance";
import { message } from "antd";

const OrderContext = createContext();

// Shared helper — lives here so context can enrich parts data
const getCompanyName = (rec) =>
  rec?.company_name ||
  rec?.customer_company ||
  rec?.customerCompany ||
  rec?.user_company ||
  rec?.user?.company_name ||
  rec?.user?.companyName ||
  rec?.customer?.company_name ||
  rec?.customer?.companyName ||
  "-";

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [deliveryStatuses, setDeliveryStatuses] = useState([]);
  const [manufacturingStatuses, setManufacturingStatuses] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // ✅ Parts state lives in context — survives component remounts
  const [orderParts, setOrderParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  // ✅ This ref lives in context, NOT in the component — never resets on navigation
  const partsFetchedRef = useRef(false);

  const userRole = localStorage.getItem("userRole") || "admin";
  const isSales = userRole === "sales";
  const isOrderCanceller = userRole === "order_canceller";

  const productionStatusLabels = {
    1: "Pending",
    2: "In Progress",
    3: "Completed",
    4: "Cancelled",
  };

  const fetchOrderDocuments = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const docRes = await axiosInstance.get(`/order/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (docRes.data.success) return docRes.data.documents;
    } catch {
      return [];
    }
    return [];
  };

  const fetchOrderParts = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axiosInstance.get(`/order/${orderId}/parts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && Array.isArray(res.data?.parts)) {
        return res.data.parts;
      }
    } catch {
      return [];
    }
    return [];
  };

  // ✅ Fetches parts for all orders — called ONCE after initial load only
const fetchAllOrderParts = async () => {
  setPartsLoading(true);

  try {
    const res = await axiosInstance.get("/order/all-parts");

    if (res.data?.success && Array.isArray(res.data.parts)) {
      const allParts = res.data.parts.map((p, index) => ({
        ...p,
        key: index,
        orderId: p.orderId,
        customer: getCompanyName(p.order),
        date: p.order?.orderDate,
      }));

      setOrderParts(allParts);
    } else {
      setOrderParts([]);
    }
  } catch (err) {
    console.error("Error fetching all order parts:", err);
    setOrderParts([]);
  } finally {
    setPartsLoading(false);
  }
};
  const singularizeCategory = (raw) => {
    const s = String(raw || "").trim();
    const low = s.toLowerCase();
    if (low === "beds") return "Bed";
    if (low === "mattresses") return "Mattress";
    if (low === "sofas") return "Sofa";
    if (low === "headboards") return "Headboard";
    if (low === "footstools") return "Footstool";
    return s || "Other";
  };

  const choosePrimaryCategory = (names) => {
    const lows = names.map((n) => String(n || "").toLowerCase());
    if (lows.includes("beds")) return "Bed";
    if (lows.includes("mattresses")) return "Mattress";
    return singularizeCategory(names[0] || "Other");
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/order/orders", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(data.orders)) {
        const ordersWithDocs = await Promise.all(
          data.orders.map(async (order) => {
            const shouldFetchDocs = Boolean(
              order?.hasDocuments === true ||
              (typeof order?.document_count === "number" && order.document_count > 0) ||
              (Array.isArray(order?.documents) && order.documents.length > 0)
            );

            const documents = shouldFetchDocs
              ? await fetchOrderDocuments(order.id)
              : [];

            const rawCats =
              order.product_categories && Array.isArray(order.product_categories)
                ? order.product_categories
                : (order.product?.Categories || []).map((c) => c.category_name);

            const productCategoryDisplay =
              order.productCategoryDisplay || choosePrimaryCategory(rawCats);

            return {
              ...order,
              order_date: order.orderDate,
              referenceNumber: order.referenceNumber || "",
              documents,
              deliveryStatus: order.deliveryStatus,
              deliveredAt: order.deliveredAt || order.delivered_at || null,
              scheduleDate: order.scheduleDate || order.schedule_date || "",
              productionStatus: order.productionStatus,
              priority: order.priority,
              trackingId: order.trackingId || "N/A",
              product_image: order.product_image || "",
              product_name: order.product_name || order.productName || "",
              total_price: order.total_price || "",
              factory_price: order.factoryPrice || order.factory_price || "",
              full_name: order.full_name || "",
              cancellationReason: order.cancellationReason || order.cancelReason || "",
              product_categories: rawCats,
              productCategoryDisplay,
              product: order.product
                ? { ...order.product, stock: order.product.stock || 0 }
                : null,
            };
          })
        );

        setOrders(ordersWithDocs);
        setPagination((prev) => ({ ...prev, total: ordersWithDocs.length }));

        // ✅ Only fetch parts once — on the very first successful orders load
        // fetchAllOrderParts guards itself with partsFetchedRef, so safe to call here

      } else {
        setOrders([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (err) {
      setOrders([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
      if (err.response?.status !== 404) {
        message.error(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to load orders."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryStatuses = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data, status } = await axiosInstance.get("/order/delivery/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (status === 200 && Array.isArray(data?.deliveryStatuses)) {
        setDeliveryStatuses(data.deliveryStatuses);
      }
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load delivery statuses."
      );
    }
  };

  const fetchManufacturingStatuses = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data, status } = await axiosInstance.get("/order/manufacturing/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (status === 200 && Array.isArray(data?.manufacturingStatuses)) {
        setManufacturingStatuses(data.manufacturingStatuses);
      }
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load manufacturing statuses."
      );
    }
  };

  const updateOrderField = async (url, payload, successMessage = "Updated") => {
    try {
      const token = localStorage.getItem("token");
      const { data, status } = await axiosInstance.put(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const ok =
        (status >= 200 && status < 300 && data?.success !== false) ||
        data?.success === true;

      if (ok) {
        message.success(data?.message || successMessage);
        fetchOrders(); // ✅ fetchAllOrderParts inside is guarded — won't re-fire
        return true;
      }

      message.error(data?.message || "Update failed.");
      return false;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Update failed."
      );
      return false;
    }
  };

  const updateOrderStatus = (orderId, status) =>
    updateOrderField(`/order/status/${orderId}`, { status }, "Status updated");

  const updateOrderPriority = (orderId, priority) =>
    updateOrderField(`/order/priority/${orderId}`, { priority }, "Priority updated");

  const updateDeliveryStatus = (orderId, status) =>
    updateOrderField(`/order/delivery/status`, { id: orderId, status }, "Delivery status updated");

  const updateManufacturingStatus = (orderId, status) =>
    updateOrderField(
      `/order/manufacturing/status`,
      { id: orderId, manufacturingStatus: status },
      "Production status updated"
    );

  const cancelOrderWithReason = async (orderId, reason) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.put(
        `/order/cancel-with-reason/${orderId}`,
        { reason, status: "cancelled", cancelReason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data?.success) {
        message.success(data?.message || "Order cancelled successfully");
        fetchOrders();
        return true;
      }

      message.error(data?.message || "Failed to cancel order");
      return false;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to cancel order"
      );
      return false;
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(`/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Order #${orderId} deleted.`);
      fetchOrders();
      return true;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to delete order."
      );
      return false;
    }
  };

  const scheduleOrders = async (orderIds, scheduleDate) => {
    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        orderIds.map((id) =>
          axiosInstance.put(
            `/order/schedule-date/${id}`,
            { scheduleDate: scheduleDate.toISOString(), deliveryStatus: "scheduled" },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      message.success("Orders scheduled successfully!");
      setSelectedRowKeys([]);
      setSelectedRows([]);
      fetchOrders();
      return true;
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Scheduling failed."
      );
      return false;
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchOrders(),
        fetchDeliveryStatuses(),
        fetchManufacturingStatuses(),
      ]);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
useEffect(() => {
  if (!orders || orders.length === 0) return;

  // stop multiple calls
  if (partsFetchedRef.current) return;

  partsFetchedRef.current = true;

  fetchAllOrderParts(orders);
}, [orders]);
  return (
    <OrderContext.Provider
      value={{
        orders,
        loading,
        pagination,
        setPagination,
        deliveryStatuses,
        manufacturingStatuses,
        productionStatusLabels,
        selectedRows,
        selectedRowKeys,
        setSelectedRows,
        setSelectedRowKeys,
        userRole,
        isSales,
        isOrderCanceller,
        // ✅ Parts exposed from context — no component-level fetching needed
        orderParts,
        partsLoading,
        fetchOrders,
        fetchOrderDocuments,
        fetchOrderParts,
        updateOrderStatus,
        updateOrderPriority,
        updateDeliveryStatus,
        updateManufacturingStatus,
        cancelOrderWithReason,
        deleteOrder,
        scheduleOrders,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};